import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toastWriteFailure } from "./writeFailure";
import { BASE_API_URL_V2 } from "@/constants";
import fpFetch from "@/services/fpFetch";
import { EntityKind } from "../Dashboard/types";
import { updateHoldingsCells } from "../Dashboard/api";

// API base URL
const API_BASE = BASE_API_URL_V2;

// ============= Types =============

export interface HoldingsColumn {
  id: number;
  name: string;
  position: number;
  data_type: "string" | "number" | "percentage" | "boolean" | "date";
  description?: string | null;
}

export interface HoldingsColumnSchema {
  columns: HoldingsColumnDefinition[];
}

export interface HoldingsColumnDefinition {
  id?: number | null;
  name: string;
  position: number;
  data_type: "string" | "number" | "percentage" | "boolean" | "date";
  description?: string | null;
}

export interface ShallowEntity {
  id: string;
  kind: EntityKind;
  name: string;
  reasoning?: string | null;
}

export interface HoldingsCellValue {
  value?: any | null;
  source_document_id?: string | null;
  source_page_num?: number | null;
}

export interface HoldingsRowData {
  row_id: number;
  position: number;
  referenced_entity?: ShallowEntity | null;
  /** The source's own name for the position (a cash line, a transfer account,
   *  a row the chat agent wrote), kept whether or not it links a record. */
  label?: string | null;
  cells: Record<number, HoldingsCellValue>; // column_id -> cell data
}

export interface HoldingsSnapshotSummary {
  id: number;
  entity: ShallowEntity;
  snapshot_date: string; // ISO date
  created_at: string; // ISO datetime
  /** Null on a snapshot a data connection extracted: no person created it. */
  created_by_user: string | null;
  row_count: number;
  /** False when a data connection maintains this snapshot and rebuilds it on
   *  every sync, so the write paths refuse edits and deletes to it. */
  is_editable: boolean;
}

export interface HoldingsSnapshotDetail {
  id: number;
  entity: ShallowEntity;
  snapshot_date: string; // ISO date
  created_at: string; // ISO datetime
  created_by_user: string | null;
  rows: HoldingsRowData[];
  is_editable: boolean;
}

export interface HoldingsSnapshotCreatePayload {
  entity_public_id: string;
  snapshot_date: string; // ISO date
  rows: HoldingsRowCreatePayload[];
  /** Start with this snapshot's positions (record and label, in order), their
   *  values blank. The same record's snapshot only (#14991). */
  copy_positions_from_snapshot_id?: number;
}

/** One position added to a snapshot through the row route (#14991): it needs
 *  a record or a label. */
export interface HoldingsRowAddPayload {
  referenced_entity_public_id?: string;
  label?: string;
  cells?: Record<number, HoldingsCellCreatePayload>;
}

export interface HoldingsRowCreatePayload {
  position: number;
  referenced_entity_public_id?: string | null;
  /** Stored as sent, with or without a linked record. */
  label?: string | null;
  cells: Record<number, HoldingsCellCreatePayload>; // column_id -> cell data
}

export interface HoldingsCellCreatePayload {
  value?: any | null;
  source_document_id?: string | null;
  source_page_num?: number | null;
}

// ============= API Functions =============

export const fetchHoldingsColumns = async (
  organizationId?: string
): Promise<HoldingsColumn[]> => {
  const params = organizationId
    ? new URLSearchParams({ organization_id: organizationId })
    : "";
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/holdings/columns?${params}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) throw new Error("Failed to fetch holdings columns");
  return response.json();
};

export const updateHoldingsColumns = async (
  payload: HoldingsColumnSchema,
  organizationId?: string
): Promise<HoldingsColumn[]> => {
  const params = organizationId ? `?organization_id=${organizationId}` : "";
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/holdings/columns${params}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    // Surface the backend's own reason -- a 403 from require_rms_edit, an
    // "Invalid data_type", a name collision. The editor shows this inline, and a
    // generic sentence there would hide which of those happened.
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail ?? "Failed to update holdings columns");
  }
  return response.json();
};

export const fetchHoldingsSnapshots = async (
  entityPublicId?: string,
  organizationId?: string
): Promise<HoldingsSnapshotSummary[]> => {
  const params = new URLSearchParams();
  if (entityPublicId) params.append("entity_public_id", entityPublicId);
  if (organizationId) params.append("organization_id", organizationId);

  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/holdings/snapshots?${params}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) throw new Error("Failed to fetch holdings snapshots");
  return response.json();
};

export const createHoldingsSnapshot = async (
  payload: HoldingsSnapshotCreatePayload,
  organizationId?: string
): Promise<HoldingsSnapshotDetail> => {
  const params = organizationId ? `?organization_id=${organizationId}` : "";
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/holdings/snapshots${params}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Failed to create holdings snapshot");
  }
  return response.json();
};

export const deleteHoldingsSnapshot = async (
  snapshotId: number,
  organizationId?: string
): Promise<void> => {
  const params = organizationId ? `?organization_id=${organizationId}` : "";
  const response = await fpFetch(
    `${API_BASE}/api/v2/rms/holdings/snapshots/${snapshotId}${params}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  if (!response.ok) throw new Error("Failed to delete holdings snapshot");
};

// ============= React Query Hooks =============

export const HOLDINGS_QUERY_KEYS = {
  all: ["holdings"] as const,
  columns: (organizationId?: string) =>
    [...HOLDINGS_QUERY_KEYS.all, "columns", organizationId] as const,
  snapshots: (entityPublicId?: string, organizationId?: string) =>
    [
      ...HOLDINGS_QUERY_KEYS.all,
      "snapshots",
      entityPublicId,
      organizationId,
    ] as const,
};

/** Invalidate every holdings query and the grids that read holdings rows.
 *
 * A snapshot write changes what the record's Holdings tab and the global
 * Holdings view show, and both read the RMS search (`["rms", "search", ...]`),
 * not the holdings keys: refreshing only those left a removed position on the
 * tab until a reload (#14991). The footer's total is its own count query
 * (`["rms", "count", ...]`), so it is refreshed beside the search. The
 * predicate is the transactions hooks' own. */
export function invalidateHoldingsViews(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.invalidateQueries({ queryKey: HOLDINGS_QUERY_KEYS.all });
  queryClient.invalidateQueries({
    predicate: (q) =>
      q.queryKey[0] === "rms" &&
      (q.queryKey[1] === "search" || q.queryKey[1] === "count"),
  });
}

export const useHoldingsColumns = (organizationId?: string) => {
  return useQuery({
    queryKey: HOLDINGS_QUERY_KEYS.columns(organizationId),
    queryFn: () => fetchHoldingsColumns(organizationId),
    staleTime: 60 * 1000, // 1 minute
  });
};

export const useUpdateHoldingsColumns = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      organizationId,
    }: {
      payload: HoldingsColumnSchema;
      organizationId?: string;
    }) => updateHoldingsColumns(payload, organizationId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: HOLDINGS_QUERY_KEYS.columns(variables.organizationId),
      });
      // The shared grid reads the holdings schema, and each fact's position,
      // which IS the configured layout, out of the RMS *bootstrap* for the
      // current org, not this editor's column list. Invalidating the bootstrap
      // is what makes an open grid re-fetch and re-derive its visible columns,
      // so a save here moves the table without a reload (#11118, as the
      // transactions editor does since #10760). The schema key is invalidated
      // too for the admin/override path that uses it. The rows need no
      // invalidation of their own: the grid's search names its columns, so a
      // changed layout sends a new search.
      queryClient.invalidateQueries({ queryKey: ["rms", "bootstrap"] });
      queryClient.invalidateQueries({ queryKey: ["rms", "schema"] });
    },
  });
};

export const useHoldingsSnapshots = (
  entityPublicId?: string,
  organizationId?: string
) => {
  return useQuery({
    queryKey: HOLDINGS_QUERY_KEYS.snapshots(entityPublicId, organizationId),
    queryFn: () => fetchHoldingsSnapshots(entityPublicId, organizationId),
    enabled: !!entityPublicId,
    staleTime: 30 * 1000, // 30 seconds
  });
};

export const useCreateHoldingsSnapshot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      organizationId,
    }: {
      payload: HoldingsSnapshotCreatePayload;
      organizationId?: string;
    }) => createHoldingsSnapshot(payload, organizationId),
    onSuccess: () => invalidateHoldingsViews(queryClient),
  });
};

export const useDeleteHoldingsSnapshot = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      snapshotId,
      organizationId,
    }: {
      snapshotId: number;
      organizationId?: string;
    }) => deleteHoldingsSnapshot(snapshotId, organizationId),
    onSuccess: () => invalidateHoldingsViews(queryClient),
    onError: toastWriteFailure,
  });
};

// ============= Row routes (#14991) =============
//
// The Holdings tab edits a snapshot one row at a time: add a position, remove
// one (the answer is the removed row, so the tab can undo by adding it back),
// and set or change the record a row points at. Each refreshes every grid
// that reads holdings, as the snapshot writes do.

const jsonOrThrow = async <T>(
  response: Response,
  fallback: string
): Promise<T> => {
  if (!response.ok) {
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail ?? fallback);
  }
  return response.json();
};

export const addHoldingsRow = async (
  snapshotId: number,
  payload: HoldingsRowAddPayload
): Promise<HoldingsRowData> =>
  jsonOrThrow(
    await fpFetch(
      `${API_BASE}/api/v2/rms/holdings/snapshots/${snapshotId}/rows`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    ),
    "Could not add the position"
  );

export const deleteHoldingsRow = async (
  rowId: number
): Promise<HoldingsRowData> =>
  jsonOrThrow(
    await fpFetch(`${API_BASE}/api/v2/rms/holdings/rows/${rowId}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    }),
    "Could not remove the position"
  );

export const setHoldingsRowRecord = async (
  rowId: number,
  referencedEntityPublicId: string | null
): Promise<HoldingsRowData> =>
  jsonOrThrow(
    await fpFetch(`${API_BASE}/api/v2/rms/holdings/rows/${rowId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        referenced_entity_public_id: referencedEntityPublicId,
      }),
    }),
    "Could not link the record"
  );

export const useAddHoldingsRow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      snapshotId,
      payload,
    }: {
      snapshotId: number;
      payload: HoldingsRowAddPayload;
    }) => addHoldingsRow(snapshotId, payload),
    onSuccess: () => invalidateHoldingsViews(queryClient),
    onError: toastWriteFailure,
  });
};

export const useDeleteHoldingsRow = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ rowId }: { rowId: number }) => deleteHoldingsRow(rowId),
    onSuccess: () => invalidateHoldingsViews(queryClient),
    onError: toastWriteFailure,
  });
};

export const useSetHoldingsRowRecord = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      rowId,
      referencedEntityPublicId,
    }: {
      rowId: number;
      referencedEntityPublicId: string | null;
    }) => setHoldingsRowRecord(rowId, referencedEntityPublicId),
    onSuccess: () => invalidateHoldingsViews(queryClient),
    onError: toastWriteFailure,
  });
};

/** One row's cells through the route the grid's cell edits use; the phone
 *  sheet edits through it. */
export const useUpdateHoldingsRowCells = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      rowId,
      cells,
    }: {
      rowId: number;
      cells: { column_id: number; value: unknown }[];
    }) => updateHoldingsCells(String(rowId), cells),
    onSuccess: () => invalidateHoldingsViews(queryClient),
    onError: toastWriteFailure,
  });
};
